import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';

export default function RecipeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [tagInput, setTagInput] = useState('');
  const [form, setForm] = useState({
    title: '', category: '', servings: 4,
    prep_time: 0, cook_time: 0,
    ingredients: [{ amount: '', unit: '', name: '' }],
    steps: [''],
    tags: [],
    is_public: false,
  });

  useEffect(() => {
    if (id) { setLoading(true); api.getRecipe(id).then(d => {
      const r = d.recipe;
      setForm({
        title:       r.title || '',
        category:    r.category || '',
        servings:    r.servings || 4,
        prep_time:   r.prep_time || 0,
        cook_time:   r.cook_time || 0,
        ingredients: r.ingredients?.length ? r.ingredients : [{ amount: '', unit: '', name: '' }],
        steps:       r.steps?.length ? r.steps : [''],
        tags:        Array.isArray(r.tags) ? r.tags : [],
        is_public:   !!r.is_public,
      });
    }).catch(() => setError('Rezept nicht gefunden')).finally(() => setLoading(false)); }
  }, [id]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Zutaten
  const setIng = (i, k, v) => { const a = [...form.ingredients]; a[i] = { ...a[i], [k]: v }; set('ingredients', a); };
  const addIng = () => set('ingredients', [...form.ingredients, { amount: '', unit: '', name: '' }]);
  const delIng = (i) => set('ingredients', form.ingredients.filter((_, j) => j !== i));

  // Schritte
  const setStep = (i, v) => { const a = [...form.steps]; a[i] = v; set('steps', a); };
  const addStep = () => set('steps', [...form.steps, '']);
  const delStep = (i) => set('steps', form.steps.filter((_, j) => j !== i));

  // Tags
  const addTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const t = tagInput.trim().toLowerCase();
      if (!form.tags.includes(t)) set('tags', [...form.tags, t]);
      setTagInput('');
    }
  };
  const delTag = (t) => set('tags', form.tags.filter(x => x !== t));

  async function handleSave(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('Titel ist erforderlich'); return; }
    setError(''); setSaving(true);
    try {
      if (isNew) { await api.createRecipe(form); navigate('/admin/recipes'); }
      else       { await api.updateRecipe(id, form); navigate('/admin/recipes'); }
    } catch(e) { setError(e.message || 'Speichern fehlgeschlagen'); }
    finally    { setSaving(false); }
  }

  // ── Styles ──
  const c = {
    wrap:    { padding: '24px 32px', maxWidth: 1200 },
    bc:      { fontSize: '.7rem', color: 'var(--admin-text-dim)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 },
    bcLink:  { color: 'var(--admin-text-muted)', textDecoration: 'none' },
    bcSep:   { color: 'var(--admin-text-dim)' },
    h1:      { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 24 },
    cols:    { display: 'flex', gap: 20, alignItems: 'flex-start' },
    main:    { flex: '1 1 0%' },
    side:    { width: 280, flexShrink: 0 },
    card:    { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, marginBottom: 16, overflow: 'hidden' },
    cardHd:  { padding: '10px 16px', background: 'var(--admin-bg-dark)', borderBottom: '1px solid var(--admin-border)', fontSize: '.72rem', fontWeight: 600, color: 'var(--admin-text-muted)', textTransform: 'uppercase', letterSpacing: '.07em' },
    cardBd:  { padding: 16 },
    lbl:     { display: 'block', fontSize: '.68rem', color: 'var(--admin-text-muted)', marginBottom: 4, marginTop: 10 },
    inp:     { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '8px 10px', fontSize: '.8rem', boxSizing: 'border-box' },
    ta:      { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '8px 10px', fontSize: '.8rem', resize: 'vertical', boxSizing: 'border-box' },
    row2:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    row3:    { display: 'grid', gridTemplateColumns: '80px 80px 1fr auto', gap: 8, alignItems: 'center', marginBottom: 6 },
    stepRow: { display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 8, alignItems: 'flex-start', marginBottom: 8 },
    stepNum: { width: 28, height: 28, background: 'var(--admin-accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.72rem', fontWeight: 700, color: 'var(--admin-bg-input)', flexShrink: 0, marginTop: 4 },
    addBtn:  { background: 'none', border: '1px dashed var(--admin-border)', color: 'var(--admin-text-dim)', borderRadius: 5, padding: '6px 12px', fontSize: '.72rem', cursor: 'pointer', width: '100%', marginTop: 6 },
    delBtn:  { background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-danger)', borderRadius: 4, padding: '4px 8px', fontSize: '.7rem', cursor: 'pointer', flexShrink: 0 },
    saveBtn: { width: '100%', background: 'var(--admin-btn-primary)', border: 'none', color: '#fff', borderRadius: 6, padding: '10px', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer', marginTop: 4 },
    cancelBtn: { width: '100%', background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 6, padding: '8px', fontSize: '.78rem', cursor: 'pointer', marginTop: 6 },
    tagWrap: { display: 'flex', flexWrap: 'wrap', gap: 5, padding: '6px 8px', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, minHeight: 36 },
    tag:     { background: 'var(--admin-tag-bg)', border: '1px solid var(--admin-border)', borderRadius: 20, padding: '2px 8px', fontSize: '.68rem', color: 'var(--admin-text)', display: 'flex', alignItems: 'center', gap: 4 },
    tagDel:  { background: 'none', border: 'none', color: 'var(--admin-text-dim)', cursor: 'pointer', fontSize: '.65rem', padding: 0, lineHeight: 1 },
    tagInp:  { background: 'none', border: 'none', color: 'var(--admin-text)', fontSize: '.75rem', outline: 'none', minWidth: 80, flex: 1 },
    togRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' },
    togLbl:  { fontSize: '.78rem', color: 'var(--admin-text-muted)' },
    errBox:  { background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 5, padding: '8px 12px', fontSize: '.75rem', color: 'var(--admin-danger)', marginBottom: 16 },
  };

  if (loading) return <div style={{...c.wrap, color:'var(--admin-text-dim)'}}>Lade Rezept...</div>;

  return (
    <div style={c.wrap}>
      {/* Breadcrumb */}
      <div style={c.bc}>
        <Link to="/admin" style={c.bcLink}>Admin</Link>
        <span style={c.bcSep}>›</span>
        <Link to="/admin/recipes" style={c.bcLink}>Rezepte</Link>
        <span style={c.bcSep}>›</span>
        <span style={{color:'var(--admin-text)'}}>{isNew ? 'Neues Rezept' : form.title || 'Bearbeiten'}</span>
      </div>

      <h1 style={c.h1}>{isNew ? '+ Neues Rezept' : 'Rezept bearbeiten'}</h1>

      {error && <div style={c.errBox}>{error}</div>}

      <form onSubmit={handleSave}>
        <div style={c.cols}>

          {/* ── Hauptspalte ── */}
          <div style={c.main}>

            {/* Titel */}
            <div style={c.card}>
              <div style={c.cardHd}>Grunddaten</div>
              <div style={c.cardBd}>
                <label style={{...c.lbl, marginTop:0}}>Titel *</label>
                <input style={c.inp} value={form.title} onChange={e => set('title', e.target.value)} placeholder="z.B. Spaghetti Carbonara" required />
                <div style={c.row2}>
                  <div>
                    <label style={c.lbl}>Vorbereitung (Min.)</label>
                    <input style={c.inp} type="number" min="0" value={form.prep_time || ''} onChange={e => set('prep_time', parseInt(e.target.value) || 0)} placeholder="z.B. 15" />
                  </div>
                  <div>
                    <label style={c.lbl}>Kochzeit (Min.)</label>
                    <input style={c.inp} type="number" min="0" value={form.cook_time || ''} onChange={e => set('cook_time', parseInt(e.target.value) || 0)} placeholder="z.B. 30" />
                  </div>
                </div>
              </div>
            </div>

            {/* Zutaten */}
            <div style={c.card}>
              <div style={c.cardHd}>Zutaten</div>
              <div style={c.cardBd}>
                {form.ingredients.map((ing, i) => (
                  <div key={i} style={c.row3}>
                    <input style={c.inp} value={ing.amount} onChange={e => setIng(i,'amount',e.target.value)} placeholder="Menge" />
                    <input style={c.inp} value={ing.unit}   onChange={e => setIng(i,'unit',e.target.value)}   placeholder="Einheit" />
                    <input style={c.inp} value={ing.name}   onChange={e => setIng(i,'name',e.target.value)}   placeholder="Zutat" />
                    <button type="button" style={c.delBtn} onClick={() => delIng(i)}>✕</button>
                  </div>
                ))}
                <button type="button" style={c.addBtn} onClick={addIng}>+ Zutat hinzufügen</button>
              </div>
            </div>

            {/* Zubereitung */}
            <div style={c.card}>
              <div style={c.cardHd}>Zubereitung</div>
              <div style={c.cardBd}>
                {form.steps.map((step, i) => (
                  <div key={i} style={c.stepRow}>
                    <div style={c.stepNum}>{i + 1}</div>
                    <textarea style={{...c.ta, minHeight:72}} value={step} onChange={e => setStep(i, e.target.value)} placeholder={`Schritt ${i+1}…`} rows={3} />
                    <button type="button" style={c.delBtn} onClick={() => delStep(i)}>✕</button>
                  </div>
                ))}
                <button type="button" style={c.addBtn} onClick={addStep}>+ Schritt hinzufügen</button>
              </div>
            </div>
          </div>

          {/* ── Seitenleiste ── */}
          <div style={c.side}>

            {/* Speichern */}
            <div style={c.card}>
              <div style={c.cardHd}>Speichern</div>
              <div style={c.cardBd}>
                <div style={c.togRow}>
                  <span style={c.togLbl}>Öffentlich sichtbar</span>
                  <input type="checkbox" checked={form.is_public} onChange={e => set('is_public', e.target.checked)} />
                </div>
                <button type="submit" style={c.saveBtn} disabled={saving}>
                  {saving ? 'Speichert…' : (isNew ? '✓ Erstellen' : '✓ Speichern')}
                </button>
                <button type="button" style={c.cancelBtn} onClick={() => navigate('/admin/recipes')}>
                  Abbrechen
                </button>
              </div>
            </div>

            {/* Metadaten */}
            <div style={c.card}>
              <div style={c.cardHd}>Metadaten</div>
              <div style={c.cardBd}>
                <label style={{...c.lbl, marginTop:0}}>Kategorie</label>
                <input style={c.inp} value={form.category} onChange={e => set('category', e.target.value)} placeholder="z.B. Pasta, Suppen…" />

                <label style={c.lbl}>Portionen</label>
                <input style={c.inp} type="number" value={form.servings} onChange={e => set('servings', parseInt(e.target.value)||1)} min="1" />

                <label style={c.lbl}>Tags <span style={{color:'var(--admin-text-dim)',fontWeight:400}}>(Enter oder Komma)</span></label>
                <div style={c.tagWrap}>
                  {form.tags.map(t => (
                    <span key={t} style={c.tag}>
                      {t}
                      <button type="button" style={c.tagDel} onClick={() => delTag(t)}>✕</button>
                    </span>
                  ))}
                  <input
                    style={c.tagInp}
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={addTag}
                    placeholder={form.tags.length === 0 ? 'Tag eingeben…' : ''}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </form>
    </div>
  );
}
